import type { PaginationState, Row, Table } from '@tanstack/react-table';
import * as React from 'react';
import type { DataGridStrings } from '../../data-grid.type';
import type { APIMeta } from '../../hooks/use-api';
import { DataTablePagination } from './data-grid-pagination/data-grid-pagination';
import { DataTableRecordCount } from './data-grid-record-count';
import { cn } from '@utils';

interface DataGridFooterProps<T> {
	meta: APIMeta;
	pagination: PaginationState;
	selectedRowCount: number;
	strings?: DataGridStrings;
	table: Table<T>;
	tableRows: Row<T>[];
}

interface LabelProps {
	meta: APIMeta;
	pagination: PaginationState;
	strings?: DataGridStrings;
}

const Label: React.FC<LabelProps> = ({ meta, pagination, strings }) => {
	// Table state is not defined yet
	if (!meta || !pagination) {
		return null;
	}

	const { pageIndex } = pagination;
	const pageSize = meta.itemsPerPage;
	const totalItems = meta.totalItems;
	const startItem = pageIndex * pageSize + 1;
	const endItem = Math.min(startItem + pageSize - 1, totalItems);

	if (strings?.footer?.showingItemsRange) {
		const { text, variables } = strings.footer.showingItemsRange;
		return (
			<div className="text-[1.2rem] text-tertiary">
				{text
					.replace(variables.start, startItem.toString())
					.replace(variables.end, endItem.toString())
					.replace(variables.total, totalItems.toString())
					.replace(variables.subject, strings?.subject || '')}
			</div>
		);
	}

	// Fallback if strings are not provided
	return (
		<div className="text-[1.2rem] text-tertiary">
			Showing {startItem} to {endItem} of {totalItems} {strings?.subject}
		</div>
	);
};

export const DataGridFooter: React.FC<DataGridFooterProps<any>> = ({
	meta,
	pagination,
	selectedRowCount,
	strings,
	table,
	tableRows,
}) => {
	if (!table || !tableRows || meta.totalItems === 0) {
		return null;
	}

	return (
		<div data-aid="data-grid-footer" className={cn('flex w-full justify-between py-2 align-middle')}>
			<div className={cn('flex')}>
				{strings?.subject && <Label meta={meta} pagination={pagination} strings={strings} />}
				<DataTableRecordCount>
					{selectedRowCount
						? strings?.footer?.selected
							? `(${selectedRowCount} ${strings.footer.selected})`
							: `(${selectedRowCount} selected)`
						: undefined}
				</DataTableRecordCount>
			</div>

			<DataTablePagination table={table} />
		</div>
	);
};
